import { classerStatut, delaiAvantReessai, type ChatErrorKind } from './reessai';

declare const __GENIOUS_API_BASE__: string;

function detectRuntimeApi(): string {
  try {
    if (typeof __GENIOUS_API_BASE__ === 'string' && __GENIOUS_API_BASE__) {
      return __GENIOUS_API_BASE__.replace(/\/$/, '');
    }
  } catch {
    /* build define absent */
  }

  // Hors navigateur (tests en environnement `node`) : pas de document à lire.
  // Sans cette garde, importer ce module suffisait à lever une ReferenceError, et
  // `sendChat` — le chemin de chaque question — ne pouvait pas être testé.
  if (typeof document === 'undefined') return 'https://api.genious-media.com';

  const script = document.currentScript as HTMLScriptElement | null;
  const fromAttr = script?.getAttribute('data-api-url');
  if (fromAttr) return fromAttr.replace(/\/$/, '');

  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://127.0.0.1:4211';
  }

  return 'https://api.genious-media.com';
}

export const API_BASE = detectRuntimeApi();

export type SiteBootstrap = {
  siteId: string;
  sessionId: string;
  /**
   * Identifiant du visiteur, renvoyé par l'API. Conservé pour que la personne
   * puisse DÉSIGNER ses données si elle veut exercer ses droits : un identifiant
   * généré côté serveur et jamais montré rend le droit d'accès théorique.
   *
   * Il n'est volontairement PAS persisté (ni cookie, ni localStorage) : il meurt
   * avec l'onglet. Le persister pour « mieux répondre » reviendrait à créer un
   * traceur là où il n'y en a aucun — augmenter la collecte au nom de la
   * conformité. La contrepartie assumée : les droits ne portent que sur la
   * session en cours.
   */
  visitorId?: string;
  /**
   * Messages de la conversation en cours, dans leur ordre d'écriture.
   *
   * Renseigné par l'API uniquement sur une REPRISE (même onglet, après un
   * rechargement) ; vide au premier chargement. L'API décide déjà quoi rendre,
   * dans quel ordre et jusqu'où — ici on ne fait que peindre.
   */
  /**
   * `admin` : un conseiller humain a repondu pendant une prise en main. Le
   * type l'omettait, et un message de conseiller restaure apres rechargement
   * s'affichait donc sans sa mention, comme s'il venait de l'IA.
   */
  messages: Array<{ role: 'user' | 'assistant' | 'admin'; content: string; grounded: boolean }>;
  /** true si la session a réellement été obtenue de l'API. */
  online: boolean;
  siteStatus?: string;
  agent: {
    name: string;
    welcome: string;
    personality?: string;
    bubbles?: string[];
  };
};

export type { ChatErrorKind };

export class ChatError extends Error {
  kind: ChatErrorKind;
  /** Attente imposée avant un nouvel essai (limite de débit), 0 sinon. */
  retryAfterMs: number;
  constructor(kind: ChatErrorKind, message: string, retryAfterMs = 0) {
    super(message);
    this.kind = kind;
    this.retryAfterMs = retryAfterMs;
  }
}

/**
 * Silence maximal toléré : avant les en-têtes de la réponse, puis entre deux
 * paquets du flux.
 *
 * Un filet, pas une limite : le socle coupe déjà après 35 s sans donnée, et
 * l'API le signale. Ce délai attrape ce qu'aucun serveur ne peut signaler — une
 * connexion morte en silence, typiquement sur mobile. Sans lui, le « … » restait
 * affiché pour toujours et bloquait tout nouvel envoi.
 */
export const DELAI_SILENCE_MS = 60_000;

export type ChatAction = {
  type: 'scroll' | 'highlight' | 'point' | 'navigate' | 'custom';
  selector?: string;
  href?: string;
  phrase?: string;
  name?: string;
  payload?: Record<string, unknown>;
  risk?: 'low' | 'medium' | 'high';
};

export type ChatResponse = {
  reply: string;
  /** La réponse enregistrée : c'est elle, et non le texte, que lit la voix. */
  messageId?: string;
  /** La voix est venue dans le flux lui-même : rien à relire après coup. */
  voixEnFlux?: boolean;
  actions?: ChatAction[];
  grounded?: boolean;
  /**
   * Un conseiller tient la main : le message est transmis, aucune reponse
   * automatique ne viendra. Le widget doit le DIRE — sans ce drapeau, le
   * visiteur voit son message partir dans le vide.
   */
  enAttenteHumain?: boolean;
  /** Sa demande de conseiller vient d'etre enregistree. */
  demandeHumainRecue?: boolean;
  /** Un operateur est-il reellement a l'ecoute a cet instant ? */
  operateurEnLigne?: boolean;
};

export type PageContext = {
  url: string;
  /** Absent dans l'aperçu du Studio : illisible dans le cadre du site. */
  title?: string;
  landmarks: Array<{ selector: string; text: string; tag: string; href?: string }>;
};

type PublicConfig = {
  name?: string;
  welcomeMessage?: string;
  proactiveBubbles?: string[];
  personality?: string;
};

export async function fetchBootstrap(
  publicId: string,
  resumeSessionId?: string
): Promise<SiteBootstrap> {
  try {
    const sessionRes = await fetch(`${API_BASE}/runtime/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        publicId,
        channel: 'text',
        origin: window.location.origin,
        resumeSessionId: resumeSessionId || undefined,
      }),
    });

    if (sessionRes.ok) {
      const session = (await sessionRes.json()) as {
        sessionId: string;
        visitorId?: string;
        siteStatus?: string;
        genious?: PublicConfig;
        messages?: SiteBootstrap['messages'];
      };
      const g = session.genious || {};
      return {
        siteId: publicId,
        sessionId: session.sessionId,
        visitorId: session.visitorId,
        // Repli sur [] : une API plus ancienne que ce widget ne renvoie pas le
        // champ, et un widget servi par CDN survit plus longtemps qu'un déploiement.
        messages: Array.isArray(session.messages) ? session.messages : [],
        online: true,
        siteStatus: session.siteStatus,
        agent: {
          name: g.name || 'GENIOUS',
          welcome:
            g.welcomeMessage ||
            'Bonjour — je suis GENIOUS, votre vendeur numérique. Je peux vous conseiller et vous emmener sur les bonnes pages.',
          bubbles: g.proactiveBubbles,
          personality: g.personality,
        },
      };
    }
  } catch {
    /* fallback below */
  }

  return {
    siteId: publicId,
    sessionId: '',
    messages: [],
    online: false,
    agent: {
      name: 'GENIOUS',
      welcome:
        'Bonjour — je suis GENIOUS, votre vendeur numérique. Je peux vous conseiller et vous emmener sur les bonnes pages.',
      bubbles: [
        'Je peux vous emmener sur la bonne page.',
        'Besoin d’un conseil avant de décider ?',
      ],
    },
  };
}

export async function sendChat(
  publicId: string,
  sessionId: string,
  message: string,
  opts?: {
    pageContext?: PageContext;
    /**
     * Reçoit le texte au fil de l'eau (W3-2). Sa présence demande le flux ;
     * l'API reste libre de répondre en JSON (clarification, API plus ancienne) :
     * c'est le `Content-Type` qui décide, pas ce qu'on a demandé.
     */
    onFragment?: (texte: string) => void;
    /**
     * La voix pendant l'écriture (lot 1). Sa présence la demande ; l'audio
     * continue d'arriver APRÈS que la réponse a été rendue.
     */
    voix?: EcouteurVoix;
  }
): Promise<ChatResponse> {
  // Pas de session : l'API était injoignable au chargement. Classée `session`
  // pour qu'un nouvel essai en rouvre une, au lieu d'échouer à l'infini.
  if (!sessionId) {
    throw new ChatError('session', 'Session indisponible');
  }

  const controleur = new AbortController();
  let lecteur: ReadableStreamDefaultReader<Uint8Array> | undefined;
  let minuterie: ReturnType<typeof setTimeout> | undefined;
  let delaiDepasse = false;
  // Réarmée à chaque signe de vie. Annuler le lecteur, et pas seulement le
  // signal : selon le navigateur, abandonner le `fetch` ne débloque pas une
  // lecture déjà en attente (constaté côté API au lot streaming).
  const armer = () => {
    clearTimeout(minuterie);
    minuterie = setTimeout(() => {
      delaiDepasse = true;
      controleur.abort();
      void lecteur?.cancel().catch(() => undefined);
    }, DELAI_SILENCE_MS);
  };

  try {
    armer();
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/runtime/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          publicId,
          sessionId,
          message,
          origin: window.location.origin,
          pageContext: opts?.pageContext,
          stream: Boolean(opts?.onFragment),
          voix: Boolean(opts?.onFragment && opts?.voix),
        }),
        signal: controleur.signal,
      });
    } catch {
      throw new ChatError('offline', delaiDepasse ? 'Aucune réponse du service' : 'Réseau indisponible');
    }

    if (!res.ok) {
      const kind = classerStatut(res.status);
      throw new ChatError(kind, `Erreur ${res.status}`, delaiAvantReessai(kind, res.headers.get('retry-after')));
    }

    armer();
    const enFlux = (res.headers.get('content-type') || '').indexOf('text/event-stream') !== -1;
    let data: CorpsChat;
    try {
      if (enFlux && res.body && opts?.onFragment) {
        lecteur = res.body.getReader();
        data = await lireFlux(lecteur, opts.onFragment, armer, opts.voix);
      } else {
        data = (await res.json()) as CorpsChat;
      }
    } catch (erreur) {
      // Coupé par notre délai : c'est la connexion qui s'est tue, pas le
      // service qui a mal répondu.
      if (delaiDepasse) throw new ChatError('offline', 'Plus de nouvelles du service');
      throw erreur instanceof ChatError ? erreur : new ChatError('server', 'Réponse illisible');
    }
    return {
      // Repli muet SAUF en attente humaine : mettre « Je suis là pour vous
      // aider » sous un message transmis a un conseiller ferait croire que
      // l'IA a repondu, alors qu'elle s'est justement tue.
      reply: data.enAttenteHumain ? '' : data.content || 'Je suis là pour vous aider.',
      messageId: typeof data.messageId === 'string' ? data.messageId : undefined,
      voixEnFlux: Boolean(enFlux && opts?.voix),
      grounded: Boolean(data.grounded ?? (data.citations?.length ?? 0) > 0),
      actions: data.actions || [],
      enAttenteHumain: Boolean(data.enAttenteHumain),
      demandeHumainRecue: Boolean(data.demandeHumainRecue),
      operateurEnLigne: Boolean(data.operateurEnLigne),
    };
  } finally {
    clearTimeout(minuterie);
  }
}

type CorpsChat = {
  content?: string | null;
  messageId?: string | null;
  citations?: unknown[];
  actions?: ChatAction[];
  grounded?: boolean;
  enAttenteHumain?: boolean;
  demandeHumainRecue?: boolean;
  operateurEnLigne?: boolean;
};

/**
 * Lit la réponse en flux et rend le corps final, celui de l'événement `fin`.
 *
 * Boucle `reader.read()` et non `for await` : un `ReadableStream` n'est pas
 * itérable dans Safari, et le widget tourne sur les sites de nos clients, donc
 * dans tous les navigateurs de leurs visiteurs.
 *
 * Analyseur SSE minimal écrit ici plutôt que partagé avec l'API : le widget ne
 * dépend d'aucun autre code du dépôt (Shadow DOM, IIFE, aucune dépendance).
 * L'API ne lui envoie que trois événements simples.
 */
/**
 * Ce que le widget fait de la voix qui arrive dans le flux (lot 1).
 * `signal` : abandonné, il coupe la lecture du flux — nouvelle question, voix éteinte.
 */
export type EcouteurVoix = {
  signal?: AbortSignal;
  /** Un morceau d'audio : PCM 16 bits petit-boutiste, mono, 24 kHz, en base64. */
  surAudio: (base64: string) => void;
  /** La réponse finale a retiré ce qui était dit : se taire. */
  surStop: () => void;
  /** Plus d'audio à venir. Peut être appelée deux fois : à rendre idempotente. */
  surFin: () => void;
};

export async function lireFlux(
  lecteur: ReadableStreamDefaultReader<Uint8Array>,
  onFragment: (texte: string) => void,
  /** Appelée à chaque paquet reçu : un flux qui avance n'est jamais coupé. */
  signeDeVie: () => void,
  voix?: EcouteurVoix
): Promise<CorpsChat> {
  const decodeur = new TextDecoder();
  let tampon = '';
  let fin: CorpsChat | null = null;
  // La réponse est rendue DÈS `fin` : la voix, elle, peut encore parler
  // plusieurs secondes. Attendre la fin du flux retarderait le texte final, le
  // guidage et le bouton d'envoi d'autant.
  let rendreFin: (corps: CorpsChat) => void = () => undefined;
  const finRecue = new Promise<CorpsChat>((resoudre) => {
    rendreFin = resoudre;
  });
  voix?.signal?.addEventListener('abort', () => void lecteur.cancel().catch(() => undefined), {
    once: true,
  });

  const traiter = (bloc: string) => {
    let evenement = 'message';
    const donnees: string[] = [];
    for (const ligne of bloc.split(/\r\n|\n|\r/)) {
      if (ligne.indexOf('event:') === 0) evenement = ligne.slice(6).trim();
      else if (ligne.indexOf('data:') === 0) donnees.push(ligne.slice(5).replace(/^ /, ''));
    }
    if (!donnees.length) return;
    let valeur: Record<string, unknown>;
    try {
      valeur = JSON.parse(donnees.join('\n')) as Record<string, unknown>;
    } catch {
      return;
    }
    if (evenement === 'fragment' && typeof valeur.texte === 'string') onFragment(valeur.texte);
    else if (evenement === 'fin') {
      fin = valeur as CorpsChat;
      rendreFin(fin);
    } else if (evenement === 'voix' && typeof valeur.audio === 'string') voix?.surAudio(valeur.audio);
    else if (evenement === 'voix-stop') voix?.surStop();
    else if (evenement === 'voix-fin') voix?.surFin();
    else if (evenement === 'erreur') throw new ChatError('server', 'Réponse interrompue');
  };

  const lecture = (async (): Promise<CorpsChat> => {
    try {
      for (;;) {
        const { done, value } = await lecteur.read();
        if (done) break;
        signeDeVie();
        // `stream: true` : un « é » coupé entre deux paquets ne devient pas « � ».
        tampon += decodeur.decode(value, { stream: true });
        let separation = tampon.search(/\r\n\r\n|\n\n|\r\r/);
        while (separation !== -1) {
          const longueur = tampon.slice(separation).match(/^(\r\n\r\n|\n\n|\r\r)/)![0].length;
          traiter(tampon.slice(0, separation));
          tampon = tampon.slice(separation + longueur);
          separation = tampon.search(/\r\n\r\n|\n\n|\r\r/);
        }
      }
      tampon += decodeur.decode();
      if (tampon.trim()) traiter(tampon);
    } catch (erreur) {
      // Après `fin`, une coupure n'emporte que la voix : la réponse est déjà là.
      if (fin) return fin;
      if (erreur instanceof ChatError) throw erreur;
      throw new ChatError('offline', 'Connexion coupée pendant la réponse');
    } finally {
      voix?.surFin();
    }
    if (!fin) throw new ChatError('server', 'Réponse interrompue');
    return fin;
  })();

  return Promise.race([finRecue, lecture]);
}

/**
 * Fait comprendre au service ce que le visiteur vient de dire (core-ai D46).
 *
 * Rend le texte compris (vide : rien entendu), ou `null` si le service ne
 * répond pas — l'appelant le dit au visiteur, qui peut toujours écrire.
 */
export async function transcrire(
  publicId: string,
  sessionId: string,
  enregistrement: Blob
): Promise<string | null> {
  if (!sessionId) return null;
  const formulaire = new FormData();
  formulaire.append('publicId', publicId);
  formulaire.append('sessionId', sessionId);
  formulaire.append('origin', window.location.origin);
  const extension = enregistrement.type.indexOf('mp4') !== -1 ? 'mp4' : 'webm';
  formulaire.append('fichier', enregistrement, `micro.${extension}`);
  try {
    const res = await fetch(`${API_BASE}/runtime/transcrire`, { method: 'POST', body: formulaire });
    if (!res.ok) return null;
    const data = (await res.json()) as { texte?: unknown };
    return typeof data.texte === 'string' ? data.texte : '';
  } catch {
    return null;
  }
}

/**
 * Le visiteur demande explicitement un humain.
 *
 * Volontairement silencieuse en cas d'echec : si l'appel ne passe pas, le
 * visiteur peut toujours ECRIRE sa demande, que la detection cote serveur
 * attrapera. Faire echouer le bouton bruyamment ajouterait une erreur a une
 * situation ou la personne cherche deja de l'aide.
 */
export async function demanderHumain(
  publicId: string,
  sessionId: string
): Promise<{ operateurEnLigne: boolean } | null> {
  if (!sessionId) return null;
  try {
    const res = await fetch(`${API_BASE}/runtime/demander-humain`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ publicId, sessionId, origin: window.location.origin }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { operateurEnLigne?: boolean };
    return { operateurEnLigne: Boolean(data.operateurEnLigne) };
  } catch {
    return null;
  }
}
